import { Form, Localization, Responsive } from 'connex-cds';
import { filter, map, find } from 'lodash';
import React, { useMemo } from 'react';
import { useVehicleTypeSetup, useVehicleSetup } from '../../../../../MasterDataProvider';
import { useTicketContext } from '../../../../../TicketContext';
import { Desktop } from './Desktop';
import { Mobile } from './Mobile';

const { Translate, useTranslateMessage } = Localization;

export const StatusTimeline = ({ data }) => {
  const { ticket } = useTicketContext();
  const { values } = Form.useFormContext();
  const translateMessage = useTranslateMessage();
  const vehicleTypeSetup = useVehicleTypeSetup();
  const vehicleSetup = useVehicleSetup();

  const primaryLineItem = useMemo(() => find(ticket?.lineItems, { isPrimary: true }), [ticket?.lineItems]);

  const vehicleTypeData = useMemo(() => {
    const targetVehicle = find(vehicleSetup.data, { id: ticket?.vehicleId });
    return find(vehicleTypeSetup.data, { crn: targetVehicle?.vehicleType?.vehicleTypeRef });
  }, [vehicleSetup.data, vehicleTypeSetup.data, ticket?.vehicleId]);

  const mergedTicketEvents = { ...ticket?.ticketEvents, ...values?.ticketEvents };

  const ticketEvents = map(mergedTicketEvents, (value, key) => ({ eventType: key, ...value }));

  const filteredEvents = filter(ticketEvents, event => {
    if (ticket?.customData?.ticketType === 'PMP') {
      switch (event?.eventType) {
        case 'PRINTED':
          return vehicleTypeData?.deliveriesConfiguration?.pump?.ticketed?.visible !== false;
        case 'TO_JOB':
          return vehicleTypeData?.deliveriesConfiguration?.pump?.toJob?.visible !== false;
        case 'ARRIVE_JOB':
          return vehicleTypeData?.deliveriesConfiguration?.pump?.arriveJob?.visible !== false;
        case 'PUMPING_STARTED':
          return vehicleTypeData?.deliveriesConfiguration?.pump?.startPump?.visible !== false;
        case 'PUMPING_COMPLETE':
          return vehicleTypeData?.deliveriesConfiguration?.pump?.finishPump?.visible !== false;
        case 'READY_TO_PUMP':
          return vehicleTypeData?.deliveriesConfiguration?.pump?.readyToPump?.visible !== false;
        case 'IN_YARD':
          return vehicleTypeData?.deliveriesConfiguration?.pump?.arrivePlant?.visible !== false;
        case 'LEAVE_JOB':
          return vehicleTypeData?.deliveriesConfiguration?.pump?.departJob?.visible !== false;
        default:
          return false;
      }
    }

    if (primaryLineItem?.productType?.systemProductType === 'AGGREGATE') {
      switch (event?.eventType) {
        case 'PRINTED':
          return vehicleTypeData?.deliveriesConfiguration?.aggregate?.ticketed?.visible !== false;
        case 'LOADING_STARTED':
          return vehicleTypeData?.deliveriesConfiguration?.aggregate?.loading?.visible !== false;
        case 'LOADING_COMPLETE':
          return vehicleTypeData?.deliveriesConfiguration?.aggregate?.loaded?.visible !== false;
        case 'TO_JOB':
          return vehicleTypeData?.deliveriesConfiguration?.aggregate?.toJob?.visible !== false;
        case 'ARRIVE_JOB':
          return vehicleTypeData?.deliveriesConfiguration?.aggregate?.arriveJob?.visible !== false;
        case 'UNLOADING':
          return vehicleTypeData?.deliveriesConfiguration?.aggregate?.beginUnload?.visible !== false;
        case 'END_UNLOADING':
          return vehicleTypeData?.deliveriesConfiguration?.aggregate?.endUnload?.visible !== false;
        case 'LEAVE_JOB':
          return vehicleTypeData?.deliveriesConfiguration?.aggregate?.departJob?.visible !== false;
        case 'IN_YARD':
          return vehicleTypeData?.deliveriesConfiguration?.aggregate?.arrivePlant?.visible !== false;
        default:
          return false;
      }
    }

    switch (event?.eventType) {
      case 'PRINTED':
        return vehicleTypeData?.deliveriesConfiguration?.readyMix?.ticketed?.visible !== false;
      case 'LOADING_STARTED':
        return vehicleTypeData?.deliveriesConfiguration?.readyMix?.loading?.visible !== false;
      case 'LOADING_COMPLETE':
        return vehicleTypeData?.deliveriesConfiguration?.readyMix?.loaded?.visible !== false;
      case 'TO_JOB':
        return vehicleTypeData?.deliveriesConfiguration?.readyMix?.toJob?.visible !== false;
      case 'ARRIVE_JOB':
        return vehicleTypeData?.deliveriesConfiguration?.readyMix?.arriveJob?.visible !== false;
      case 'UNLOADING':
        return vehicleTypeData?.deliveriesConfiguration?.readyMix?.beginUnload?.visible !== false;
      case 'END_UNLOADING':
        return vehicleTypeData?.deliveriesConfiguration?.readyMix?.endUnload?.visible !== false;
      case 'LEAVE_JOB':
        return vehicleTypeData?.deliveriesConfiguration?.readyMix?.departJob?.visible !== false;
      case 'IN_YARD':
        return vehicleTypeData?.deliveriesConfiguration?.readyMix?.arrivePlant?.visible !== false;
      case 'WASHING':
        return vehicleTypeData?.deliveriesConfiguration?.readyMix?.beginWash?.visible !== false;
      case 'END_WASHING':
        return vehicleTypeData?.deliveriesConfiguration?.readyMix?.endWash?.visible !== false;
      default:
        return false;
    }
  });

  if (
    ticket?.customData?.productDetails?.wcContactDateTime &&
    !(vehicleTypeData?.deliveriesConfiguration?.readyMix?.firstWaterCementContact?.visible === false)
  ) {
    const wCContact = {
      eventType: 'FIRST_WCCONTACT',
      eventDateTime: ticket?.customData?.productDetails?.wcContactDateTime,
    };
    filteredEvents.push(wCContact);
  }

  // We need to map our object to the requirements of the Timeline component.
  // Optionally, action can be added to zero or more items and, if present, will be rendered
  // to the right of the description.
  const items = React.useMemo(() => {
    return filteredEvents.map(event => ({
      isoDateString: event.eventDateTime,
      event: translateMessage(event.eventType),
      description: (
        <div>
          <div style={{ display: 'flex' }}>
            <div>
              <Translate stringId={event.eventType} />
            </div>
          </div>
        </div>
      ),
    }));
  }, [filteredEvents, translateMessage]);

  return (
    <Responsive>
      <Mobile items={items} />
      <Desktop items={items} />
    </Responsive>
  );
};
